import { cn } from '@heidi/utils';

import { focusRingWithin } from '../styles/focus-ring';
import { typographyVariant } from '../typography';

export const calendarRootTokensClasses =
  'group/calendar [--cell-size:2rem] [--weekday-height:1.5rem]';

export const calendarRootClasses = 'w-fit';

export const calendarMonthsClasses = (monthsLayout: 'row' | 'column') =>
  cn(
    'relative flex flex-col gap-4 md:flex-row',
    monthsLayout === 'row' ? 'flex-row' : 'flex-col'
  );

export const calendarMonthClasses = 'flex w-fit flex-col';

export const calendarNavClasses =
  'absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1';

export const calendarNavButtonClasses =
  'size-[--cell-size] select-none p-0 aria-disabled:opacity-50';

export const calendarMonthCaptionClasses =
  'flex h-[--cell-size] w-full items-center justify-center px-[--cell-size]';

export const calendarDropdownsClasses = cn(
  'flex h-[--cell-size] w-full items-center justify-center gap-1.5',
  typographyVariant({ variant: 'p2Bold' })
);

export const calendarDropdownRootClasses = cn(
  'relative rounded-md border border-border shadow-sm',
  focusRingWithin
);

export const calendarDropdownClasses = 'absolute inset-0 opacity-0';

export const calendarCaptionLabelClasses = (
  captionLayout: 'label' | 'dropdown' | 'dropdown-months' | 'dropdown-years'
) =>
  cn(
    'select-none',
    typographyVariant({ variant: 'p2Bold' }),
    captionLayout !== 'label' &&
      'flex h-8 items-center gap-1 rounded-md pe-1 ps-2 [&>svg]:size-3.5 [&>svg]:text-text-secondary'
  );

export const calendarMonthGridClasses =
  'w-[calc(7*var(--cell-size))] table-fixed border-separate border-spacing-x-0 border-spacing-y-2';

export const calendarWeekNumberHeaderClasses =
  'h-[--weekday-height] w-[--cell-size] select-none';

export const calendarWeekNumberClasses = cn(
  'select-none text-text-secondary',
  typographyVariant({ variant: 'caption' })
);

export const calendarWeekNumberCellClasses =
  'flex size-[--cell-size] items-center justify-center text-center';

export const calendarRangeStartClasses = 'rounded-s-md bg-background-secondary';

export const calendarRangeMiddleClasses = 'rounded-none';

export const calendarRangeEndClasses = 'rounded-e-md bg-background-secondary';

// Today's fill and the range shading are both the CELL background, so only one
// of them can paint. Gate today's on `:not([data-selected])` rather than
// clearing it on `[data-selected]`: a `data-[selected=true]:` override outranks
// the range fill on specificity, which blanked the half-cell shading that joins
// a range to the day it starts or ends on.
export const calendarTodayClasses =
  'text-text-primary [&:not([data-selected])]:rounded-md [&:not([data-selected])]:bg-background-secondary';

export const calendarOutsideClasses =
  'text-text-secondary aria-selected:text-text-secondary';

export const calendarDisabledClasses = 'text-text-secondary opacity-50';

export const calendarHiddenClasses = 'invisible';

export const calendarDayCellClasses =
  'group/day relative size-[--cell-size] select-none p-0 text-center [&:first-child[data-selected=true]_button]:rounded-s-md [&:last-child[data-selected=true]_button]:rounded-e-md';

export const calendarWeekdayClasses = cn(
  'h-[--weekday-height] w-[--cell-size] select-none rounded-md text-center text-text-secondary',
  typographyVariant({ variant: 'caption' })
);

export const calendarFooterClasses = 'flex w-full items-center justify-end gap-1 pt-2';

/**
 * Overlay on the DS `Button` (`ghost` / `xs` / `iconOnly`) that CalendarDayButton
 * renders. Chrome, hover, and the `border-focus` ring stay on the recipe — this
 * string is the size box, caption-weight numerals, and calendar selection fills.
 */
export const calendarDayButtonClasses = cn(
  // Size box reset — the xs recipe's h/min-w/padding/leading fight `--cell-size`.
  // `font-normal` beats `captionBold` so day numerals are caption (12px / 400).
  'size-[--cell-size] min-h-0 min-w-0 p-0 font-normal leading-none',
  // Stable ghost has no border gutter; keep one so the focus border swap paints.
  'border border-transparent',
  // The day *cell* already fades disabled dates; don't stack Button's 50%.
  'disabled:opacity-100',
  // Selection fills — keyed off data-attrs set by CalendarDayButton.
  'data-[selected-single=true]:bg-background-primary data-[selected-single=true]:text-text-primary-invert',
  'data-[selected-single=true]:hover:bg-background-primary data-[selected-single=true]:hover:text-text-primary-invert',
  'data-[selected-single=true]:active:bg-background-primary data-[selected-single=true]:active:text-text-primary-invert',
  'data-[range-start=true]:bg-background-primary data-[range-start=true]:text-text-primary-invert',
  'data-[range-start=true]:hover:bg-background-primary data-[range-start=true]:hover:text-text-primary-invert',
  'data-[range-start=true]:active:bg-background-primary data-[range-start=true]:active:text-text-primary-invert',
  'data-[range-end=true]:bg-background-primary data-[range-end=true]:text-text-primary-invert',
  'data-[range-end=true]:hover:bg-background-primary data-[range-end=true]:hover:text-text-primary-invert',
  'data-[range-end=true]:active:bg-background-primary data-[range-end=true]:active:text-text-primary-invert',
  'data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-background-secondary data-[range-middle=true]:text-text-primary',
  'data-[range-middle=true]:hover:bg-background-secondary data-[range-middle=true]:hover:text-text-primary',
  'data-[range-middle=true]:active:bg-background-secondary data-[range-middle=true]:active:text-text-primary'
);
